import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Modal, Row } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { FaDollarSign } from "react-icons/fa";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import ConfirmModal from "../../../global/components/ConfirmModal";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { PageNotFound } from "../../../global/components/PageNotFound";
import MyPaymentForm from "../../../global/components/PaymentForm";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import PaymentIcon from "../../../assets/images/vectors/SideBarIcons/PaymentIcon";
import PurchaseLicenseItemModal from "./PurchaseLicenseItemModal";
import { PurchaseLicenseItemsColumns } from "./PurchaseLicenseItemsColumns";
import { buildPurchaseItemPayload } from "./purchaseLicenseItemUtils";
import { useDealerRenewal } from "./PrepurchasedItemsForm";

const ON_SITE_PURCHASE_TYPE = 0;
const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const hasValue = (value) => value != null && value !== "";

const getPurchaseRowId = (record) =>
  record?.PurchaseNo ?? record?.purchase_no ?? record?.id;

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `$${n.toFixed(2)}`;
};

const formatDiscount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0%";
  return `${n}%`;
};

const CODE_FIELDS = ["CodeNo", "Item", "ItemName", "CodeName", "Term", "Price"];

/** Recomputes StartTime / EndTime / Renewal the same way the API payload does. */
const withSchedule = (row, { startTime, renewal }) => {
  const payload = buildPurchaseItemPayload({
    codeNo: row.CodeNo,
    term: row.Term,
    startTime,
    renewal,
  });
  return {
    ...row,
    StartTime: payload.StartTime,
    EndTime: payload.EndTime,
    Renewal: payload.Renewal,
  };
};

const toItemPayload = (row) =>
  buildPurchaseItemPayload({
    codeNo: row.CodeNo,
    term: row.Term,
    startTime: row.StartTime,
    renewal: row.Renewal,
  });

export const PurchaseLicenseForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { siteNo, deviceNo, id } = useParams();
  const isAdd = !hasValue(id);
  const isDevicePage = deviceNo != null;
  const parentNo = isDevicePage ? deviceNo : siteNo;
  const parentPath = isDevicePage ? "device" : "site";
  const parentInfoPath = `/${parentPath}/info/${parentNo}`;
  const isSystemUser = hasRole(1, 2, 3);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [showCardLayout, setShowCardLayout] = useState(!isSystemUser);
  const [pendingItems, setPendingItems] = useState([]);
  const [savedEdits, setSavedEdits] = useState({});
  const [savingItems, setSavingItems] = useState(false);
  const [paidPayment, setPaidPayment] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [createdPurchase, setCreatedPurchase] = useState(null);
  const pendingSeqRef = useRef(0);

  const parentUrl =
    parentNo != null && parentNo !== ""
      ? `${process.env.REACT_APP_API_URL}/api/${isDevicePage ? "devices" : "sites"}/${parentNo}/`
      : null;
  const {
    data: parentData,
    loading: parentLoading,
    error: parentError,
  } = useGet(parentUrl);

  const hasParentAccess = !parentError && Boolean(parentData?.data);

  const purchaseUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`
      : null;
  const itemsUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`
      : null;
  const deleteItemsUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/bulk-delete/`
      : null;
  const putUrl =
    hasParentAccess && id != null && id !== ""
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${id}/`
      : null;

  const {
    data: purchaseData,
    loading: purchaseLoading,
    error: purchaseError,
    refetch: purchaseRefetch,
  } = useGet(purchaseUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);
  const {
    data: deletedItemsData,
    error: deletedItemsError,
    deleteMultiple: deleteItemsData,
  } = useDelete(deleteItemsUrl);
  const { error: putError, putData: putAction } = usePut(putUrl);
  const { error: putItemError, putData: putItemAction } = usePut(null);
  const { error: postItemError, postData: postItemAction } = usePost(itemsUrl);
  const { error: postPurchaseError, postData: postPurchaseAction } = usePost(
    `${process.env.REACT_APP_API_URL}/api/purchases/`,
  );

  const purchase = purchaseData?.data;
  const parent = parentData?.data;
  const purchaseLoaded = isAdd
    ? hasParentAccess
    : Boolean(purchase) && !purchaseLoading;
  const dealerNo = isAdd
    ? (parent?.DealerNo ?? (hasRole(7, 8, 9) ? getDealerNo() : null))
    : (purchase?.DealerNo ?? null);
  const hasPaymentTime =
    purchase?.PaymentTime != null && purchase?.PaymentTime !== "";
  const items = useMemo(
    () =>
      (itemsData?.data ?? []).map((item) => ({
        ...item,
        key: item.ItemNo,
      })),
    [itemsData],
  );
  const tableRows = useMemo(
    () => [
      ...pendingItems,
      ...items.map((item) =>
        savedEdits[item.ItemNo]
          ? { ...item, ...savedEdits[item.ItemNo] }
          : item,
      ),
    ],
    [pendingItems, items, savedEdits],
  );
  const hasLocalChanges =
    pendingItems.length > 0 || Object.keys(savedEdits).length > 0;

  useEffect(() => {
    if (!paymentModal) setShowCardLayout(!isSystemUser);
  }, [paymentModal, isSystemUser]);

  const optionUrl = purchaseLoaded
    ? `${process.env.REACT_APP_API_URL}/api/devices/options/`
    : null;
  const { data: optionsData } = useGet(optionUrl);

  const resolveOemNo = (value) => {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const oemOption = optionsData?.data?.oems?.find(
      (oem) => String(oem?.name) === String(value),
    );
    return oemOption?.value ?? null;
  };

  const resolvedOemNo = resolveOemNo(
    isAdd ? (parent?.OemNo ?? getOemNo()) : purchase?.OemNo,
  );

  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const { data: dealerOptions } = useGet(dealerOptionsUrl);

  const isDealerUser = hasRole(7, 8, 9);
  const loggedInDealerRenewal = useDealerRenewal();
  const purchaseDealer = useMemo(
    () =>
      (Array.isArray(dealerOptions?.data) ? dealerOptions.data : []).find(
        (d) => Number(d.value) === Number(dealerNo),
      ),
    [dealerOptions, dealerNo],
  );
  const purchaseDealerRenewal =
    Number(purchaseDealer?.Renewal ?? purchaseDealer?.renewal) === 1;
  const showRenewal = isDealerUser
    ? loggedInDealerRenewal
    : purchaseDealerRenewal;

  const discountPercent = isAdd
    ? Math.min(Math.max(Number(purchaseDealer?.discount) || 0, 0), 100)
    : Number(purchase?.Discount) || 0;
  const apiSubTotal = Number(purchase?.SubTotal) || 0;
  const apiTotal = Number(purchase?.Total) || 0;
  const localPriceDelta =
    pendingItems.reduce((sum, row) => sum + (Number(row.Price) || 0), 0) +
    items.reduce((sum, item) => {
      const edit = savedEdits[item.ItemNo];
      if (!edit || edit.Price == null) return sum;
      return sum + (Number(edit.Price) || 0) - (Number(item.Price) || 0);
    }, 0);
  const subTotal = apiSubTotal + localPriceDelta;
  const total = hasLocalChanges
    ? Math.round(subTotal * (1 - discountPercent / 100) * 100) / 100
    : apiTotal;
  const purchaseAmountProp = `$${Math.max(total, 0).toFixed(2)}`;
  const showPayment =
    purchaseLoaded && !hasPaymentTime && (Boolean(paidPayment) || total > 0);
  const canManageItems = purchaseLoaded && !hasPaymentTime && !paidPayment;

  useEffect(() => {
    if (!purchaseLoaded) return;
    form.setFieldsValue({
      OemName:
        purchase?.OemNo != null && purchase?.OemNo !== ""
          ? String(purchase.OemNo)
          : "",
      DealerNo: dealerNo != null && dealerNo !== "" ? Number(dealerNo) : null,
      SubTotal: formatMoney(subTotal),
      Discount: formatDiscount(discountPercent),
      Total: formatMoney(total),
    });
  }, [
    form,
    purchaseLoaded,
    purchase,
    dealerNo,
    discountPercent,
    subTotal,
    total,
  ]);

  useEffect(() => {
    const error =
      putItemError || postItemError || putError || postPurchaseError;
    if (error) {
      openErrorNotification(
        error?.response?.data?.message || "Something went wrong",
      );
    }
  }, [putItemError, postItemError, putError, postPurchaseError]);

  useEffect(() => {
    if (deletedItemsError) {
      openErrorNotification(
        deletedItemsError?.response?.data?.message || "Something went wrong",
      );
    } else if (deletedItemsData) {
      openConfirmNotification("Success");
      setSelectedRowKeys([]);
      itemsRefetch();
      purchaseRefetch();
    }
  }, [deletedItemsData, deletedItemsError, itemsRefetch, purchaseRefetch]);

  if (
    !parentNo ||
    parentError ||
    (!parentLoading && !parentData?.data) ||
    purchaseError
  ) {
    return <PageNotFound />;
  }

  if (parentLoading || !purchaseLoaded) {
    return null;
  }

  const breadcrumbItems = [
    { label: isDevicePage ? "Device" : "Site", href: `/${parentPath}` },
    { label: "Information", href: parentInfoPath },
    {
      label: "Purchase License",
      href: isAdd
        ? `/${parentPath}/${parentNo}/purchase-license`
        : `/${parentPath}/${parentNo}/purchase-license/${id}`,
    },
  ];
  const getPaymentNo = (paymentResponse) => {
    const payment =
      paymentResponse?.data?.data ?? paymentResponse?.data ?? paymentResponse;
    return payment?.PaymentNo ?? payment?.payment_no ?? payment?.id ?? null;
  };

  const getPurchasePaidPayload = (target, paymentResponse, amount) => ({
    PurchaseTime: target?.PurchaseTime
      ? dayjs(target.PurchaseTime).format(DATE_TIME_FORMAT)
      : null,
    PurchaseType: target?.PurchaseType ?? ON_SITE_PURCHASE_TYPE,
    OemNo: resolvedOemNo,
    DealerNo: dealerNo,
    Amount: amount,
    PaymentNo: getPaymentNo(paymentResponse),
    DeviceNo: Number(parentNo),
  });

  const openAddItemModal = () => {
    setEditingItem(null);
    setItemModalOpen(true);
  };

  const openEditItemModal = (record) => {
    if (!canManageItems || !record) return;
    setEditingItem(record);
    setItemModalOpen(true);
  };

  const updateRow = (record, changes) => {
    const next = withSchedule(
      { ...record, ...changes.code },
      {
        startTime: changes.startTime ?? record.StartTime,
        renewal: changes.renewal ?? record.Renewal,
      },
    );
    if (record.isPending) {
      setPendingItems((prev) =>
        prev.map((row) => (row.key === record.key ? next : row)),
      );
      return;
    }
    const edit = {};
    [...CODE_FIELDS, "StartTime", "EndTime", "Renewal"].forEach((field) => {
      edit[field] = next[field];
    });
    setSavedEdits((prev) => ({ ...prev, [record.ItemNo]: edit }));
  };

  const onPickItem = (code) => {
    const codeFields = {};
    CODE_FIELDS.forEach((field) => {
      codeFields[field] = code[field];
    });
    if (editingItem) {
      if (Number(code.CodeNo) !== Number(editingItem.CodeNo)) {
        updateRow(editingItem, { code: codeFields });
      }
      return true;
    }
    pendingSeqRef.current += 1;
    const seq = pendingSeqRef.current;
    setPendingItems((prev) => [
      withSchedule(
        // Negative ItemNo keeps the selector's "same row" checks working before save.
        { key: `pending-${seq}`, ItemNo: -seq, isPending: true, ...codeFields },
        { startTime: null, renewal: 0 },
      ),
      ...prev,
    ]);
    return true;
  };

  const onDeleteItemsConfirm = async () => {
    const savedKeys = selectedRowKeys.filter(
      (key) => !String(key).startsWith("pending-"),
    );
    setPendingItems((prev) =>
      prev.filter((row) => !selectedRowKeys.includes(row.key)),
    );
    setSavedEdits((prev) => {
      const next = { ...prev };
      savedKeys.forEach((key) => delete next[key]);
      return next;
    });
    setShowItemsDeleteModal(false);
    if (!savedKeys.length) {
      setSelectedRowKeys([]);
      return;
    }
    try {
      await deleteItemsData({ ids: savedKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  const createPurchase = async () => {
    const PurchaseTime = dayjs().format(DATE_TIME_FORMAT);
    const created = await postPurchaseAction({
      PurchaseTime,
      PurchaseType: ON_SITE_PURCHASE_TYPE,
      OemNo: resolvedOemNo,
      DealerNo: Number(dealerNo),
    });
    const createdData = created?.data ?? created;
    if (getPurchaseRowId(createdData) == null) return null;
    const next = {
      PurchaseTime,
      PurchaseType: ON_SITE_PURCHASE_TYPE,
      ...createdData,
    };
    setCreatedPurchase(next);
    return next;
  };

  // A failed save keeps the payment and any created purchase, so a retry never charges or creates twice.
  const finalizePaidPurchase = async (paymentResponse, amount) => {
    setSavingItems(true);
    const fail = (message) => {
      setSavingItems(false);
      setPaidPayment({ response: paymentResponse, amount });
      if (!isAdd) itemsRefetch();
      openErrorNotification(message);
    };

    const target = isAdd
      ? (createdPurchase ?? (await createPurchase()))
      : purchase;
    if (!target) {
      fail(
        "Payment received, but the purchase could not be created. Click Save Items to retry.",
      );
      return;
    }
    const targetId = isAdd ? getPurchaseRowId(target) : id;
    const targetUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${targetId}/`;

    const failedPending = [];
    for (const row of pendingItems) {
      const result = await postItemAction(
        toItemPayload(row),
        `${targetUrl}items/`,
      );
      if (result == null) failedPending.push(row);
    }
    const failedEdits = {};
    for (const item of items) {
      const edit = savedEdits[item.ItemNo];
      if (!edit) continue;
      const result = await putItemAction(
        toItemPayload({ ...item, ...edit }),
        `${targetUrl}items/${item.ItemNo}/`,
      );
      if (result == null) failedEdits[item.ItemNo] = edit;
    }
    setPendingItems(failedPending);
    setSavedEdits(failedEdits);
    const itemsSaved =
      !failedPending.length && !Object.keys(failedEdits).length;
    if (!itemsSaved) {
      fail(
        "Payment received, but some items could not be saved. Click Save Items to retry.",
      );
      return;
    }

    const putResult = await putAction(
      getPurchasePaidPayload(target, paymentResponse, amount),
      targetUrl,
    );
    if (putResult == null) {
      fail(
        "Payment received, but the purchase could not be updated. Click Save Items to retry.",
      );
      return;
    }
    setSavingItems(false);
    setPaidPayment(null);
    openConfirmNotification("Success");
    if (!isAdd) purchaseRefetch();
    navigate(parentInfoPath);
  };

  const onPayment = () => {
    if (paidPayment) {
      finalizePaidPurchase(paidPayment.response, paidPayment.amount);
      return;
    }
    if (isAdd && (resolvedOemNo == null || !hasValue(dealerNo))) {
      openErrorNotification("OEM and Dealer are required.");
      return;
    }
    setPaymentModal(true);
  };

  const onCancel = () =>
    hasLocalChanges ? setShowLeaveModal(true) : navigate(parentInfoPath);

  const buttonsData = [
    ...(showPayment
      ? [
          {
            label: paidPayment ? "Save Items" : "Payment",
            icon: paidPayment ? <CheckCircleOutlined /> : <FaDollarSign />,
            onClick: onPayment,
            loading: savingItems,
            disabled: savingItems,
          },
        ]
      : []),
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onCancel,
      primary: !showPayment,
    },
  ];

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: openAddItemModal,
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowItemsDeleteModal(true),
      disabled: selectedRowKeys.length === 0,
    },
  ];

  return (
    <>
      <Form
        validateTrigger="onChange"
        form={form}
        scrollToFirstError
        layout="vertical"
        autoComplete="off"
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>

          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <PaymentIcon className="text-[18px] text-black" />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                Purchase License
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="OEM"
                    name="OemName"
                    disabled
                    loading={optionDataLoading || purchaseLoading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={modifiedDealers}
                    disabled
                    placeholder="Please select"
                    loading={dealerOptionsLoading || purchaseLoading}
                  />
                </Col> */}
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="SubTotal" name="SubTotal" disabled />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Discount" name="Discount" disabled />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput label="Total" name="Total" disabled />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <div className="flex justify-end mb-2">
        {canManageItems ? (
          <CommonButton buttonsData={itemButtonsData} />
        ) : (
          <div className="mt-2" />
        )}
      </div>
      <GlobalTable
        columns={PurchaseLicenseItemsColumns({
          showRenewal,
          editable: canManageItems,
          onStartTimeChange: (record, value) =>
            updateRow(record, { startTime: value }),
          onRenewalChange: (record, checked) =>
            updateRow(record, { renewal: checked ? 1 : 0 }),
        })}
        dataSource={tableRows}
        loading={itemsLoading}
        rowSelection={
          canManageItems
            ? {
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }
            : null
        }
        onRow={
          canManageItems
            ? (record) => ({
                onClick: (event) => {
                  const clickedControl = event.target.closest(
                    ".ant-table-selection-column, .ant-picker, .ant-switch",
                  );
                  if (
                    clickedControl ||
                    !event.currentTarget.contains(event.target)
                  ) {
                    return;
                  }
                  openEditItemModal(record);
                },
              })
            : undefined
        }
      />

      <PurchaseLicenseItemModal
        open={itemModalOpen}
        setOpen={setItemModalOpen}
        purchaseId={id}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        existingItems={tableRows}
        onPick={onPickItem}
      />
      <ConfirmModal
        showModal={showItemsDeleteModal}
        setShowModal={setShowItemsDeleteModal}
        onDeleteConfirm={onDeleteItemsConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <ConfirmModal
        showModal={showLeaveModal}
        setShowModal={setShowLeaveModal}
        onDeleteConfirm={() => navigate(parentInfoPath)}
        content="Payment has not been completed. Any changes will be discarded. Do you want to leave?"
      />

      <Modal
        title={
          <div className="flex items-center justify-between bg-gray-200 p-2.5 rounded-md">
            <div className="flex items-center">
              <PaymentIcon className="text-[18px] text-black" />
              <span className="text-[14px] font-[Poppins] text-black ml-2">
                Make Payment
              </span>
            </div>
          </div>
        }
        open={paymentModal}
        onCancel={() => {
          setPaymentModal(false);
          setShowCardLayout(!isSystemUser);
        }}
        footer={null}
        closeIcon={false}
        width={560}
        centered
        destroyOnClose
      >
        <MyPaymentForm
          DealerNo={dealerNo}
          amount={purchaseAmountProp}
          setPaymentModal={setPaymentModal}
          InvoiceNo={id}
          OrderName={
            purchase?.PurchaseTime ? String(purchase.PurchaseTime) : "Purchase"
          }
          refetch={isAdd ? undefined : purchaseRefetch}
          showCardLayout={showCardLayout}
          setShowCardLayout={setShowCardLayout}
          onPaymentSuccess={async (paymentResponse) => {
            setPaymentModal(false);
            await finalizePaidPurchase(paymentResponse, total);
          }}
        />
      </Modal>
    </>
  );
};
